/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTable, Row, useSortBy, useExpanded, Column } from "react-table";
import { useFormContext } from "react-hook-form";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { ReactComponent as CopyToClipboardIcon } from "assets/images/icons/CopyToClipboard.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/Delete.svg";
import { ReactComponent as StackableIcon } from "assets/images/icons/Stackable.svg";
import { ReactComponent as DangerousIcon } from "assets/images/icons/Dangerous.svg";
import { ReactComponent as CratingRequiredIcon } from "assets/images/icons/CratingRequired.svg";
import { ReactComponent as HazardousIcon } from "assets/images/icons/Hazardous.svg";
import { ReactComponent as TemperatureControlIcon } from "assets/images/icons/TemperatureControl.svg";
import { ReactComponent as HandlingRequiredIcon } from "assets/images/icons/HandlingRequired.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { ReactComponent as AlertTriangle } from "assets/images/iconsV2/alert-triangle.svg";

import DataTable from "components/organisms/DataTable/DataTable";
import { FormattedMessage, useIntl } from "react-intl";
import { capitalizeFirstLetter } from "helpers/commonHelpers";
import { StopType } from "types/MultiStop";
import PackingListAddItem from "./PackingListAddItem";
import { dangerousGoodsOptions, temperatureControlledOptions } from "../PackingListDefaultOptions";
import { getPackingListMissingPickupOrDeliveryWarning } from "../packingListHelpers";

function PackingListTable({
  items,
  append,
  remove,
  setSelectedItemIndex,
  handlingUnits,
  handlingRequiredSetting,
  isNMFCEnabled,
  NMFCValues,
  currencyOptions,
  packingListConfig,
  state,
  dispatch,
  expandedRow,
  setExpandedRow,
  isQuoteRequest = false,
}: any) {
  if (items.length === 0) return null;
  const intl = useIntl();
  const {
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const handlingUnit = items[0].handling_unit;
  const hasOneHsCode = watch("packing_list")?.some((item: any) => Boolean(item.good_customs_information?.[0]?.hs_code));
  const stopsRaw = watch("schedule.stops");
  const stops: any[] = useMemo(() => stopsRaw || [], [stopsRaw]);
  // WEB-11639: match PackingListAddItem / V2 — only show stop columns when >2 stops.
  const hasMultiStopStops = stops.length > 2;
  const { packingListStopWarnings, hasWarnings } = useMemo(
    () => getPackingListMissingPickupOrDeliveryWarning({ watch }),
    [stops, items],
  );

  /**
   * Toggles the property of an item, e.g. stackable, hazardous, etc.
   * @param index - the index of the item
   * @param property - the property to toggle
   */
  const toggleItemProperty = (
    index: number,
    property:
      | "hazardous"
      | "stackable"
      | "crating_required"
      | "temperature_control_required"
      | "is_dangerous_good"
      | "handling_required",
  ) => {
    // Map the property to their corresponding additional config keys
    const propertyMappings: any = {
      crating_required: "crating_required_by",
      temperature_control_required: "temperature_control_value",
      is_dangerous_good: ["dangerous_good_value", "un_number"],
    };

    // Toggle the property
    const prevValue = getValues(`packing_list[${index}].${property}`);
    setValue(`packing_list[${index}].${property}`, !prevValue);

    // If we are toggling the property on, set the corresponding global config value to true
    if (!prevValue && !getValues(`packing.global_configuration.${property}`)) {
      setValue(`packing.global_configuration.${property}`, true);
    }

    // If the property is being toggled off, clear the corresponding additional config value
    // If the property is being toggled on, set the corresponding additional config value to the global config value
    const configKeys = propertyMappings[property];
    if (configKeys) {
      const keys = Array.isArray(configKeys) ? configKeys : [configKeys];
      keys.forEach((configKey: string) => {
        if (prevValue) {
          setValue(`packing_list[${index}].${configKey}`, configKey === "un_number" ? "" : null);
        } else {
          setValue(
            `packing_list[${index}].${configKey}`,
            getValues(`packing.global_configuration.${configKey}`) ?? (configKey === "un_number" ? "" : null),
          );
        }
      });
    }
  };

  const expandedRowRef = useRef(expandedRow);
  expandedRowRef.current = expandedRow;

  const clearSelectedItemAndCollapseRows = useCallback(() => {
    setSelectedItemIndex(null);
    expandedRowRef.current?.toggleRowExpanded();
    setExpandedRow(null);
  }, []);

  const columns = useMemo(() => {
    // The columns that all packing list tables will have
    const baseColumns: Column[] = [
      {
        Header: " ",
        accessor: (row: any) => [
          row.stackable,
          row.crating_required,
          row.crating_required_by,
          row.hazardous,
          row.handling_required,
          row.is_dangerous_good,
          row.dangerous_good_value,
          row.un_number,
          row.temperature_control_required,
          row.temperature_control_value,
          row.index,
        ],
        Cell: ({ value }: any) => {
          const [
            stackable,
            cratingRequired,
            cratingRequiredBy,
            hazardous,
            handling_required,
            dangerous,
            dangerousGoodValue,
            unNumber,
            temperatureControlRequired,
            temperatureControlValue,
            index,
          ] = value;

          const selectedOption = temperatureControlledOptions(intl).find(
            (o) => o.value === temperatureControlValue?.toString(),
          );
          const selectedTempControlLabel = selectedOption?.label;
          return (
            <>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="item-tooltip">
                    <span className="font-weight-normal">Stackable</span>
                  </Tooltip>
                }
              >
                <span style={{ opacity: stackable ? 1 : 0.25 }}>
                  <StackableIcon width={18} onClick={() => toggleItemProperty(index, "stackable")} />
                </span>
              </OverlayTrigger>
              {packingListConfig.hazardous_goods_enabled && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="item-tooltip">
                      <span className="font-weight-normal">Hazardous</span>
                    </Tooltip>
                  }
                >
                  <span style={{ opacity: hazardous ? 1 : 0.25 }} className="ml-2">
                    <HazardousIcon width={16} onClick={() => toggleItemProperty(index, "hazardous")} />
                  </span>
                </OverlayTrigger>
              )}
              {packingListConfig.crating_required_enabled && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="item-tooltip">
                      <span className="font-weight-normal">Crating Required by </span>
                      <span className="font-weight-bold">{cratingRequiredBy}</span>
                    </Tooltip>
                  }
                >
                  <span style={{ opacity: cratingRequired ? 1 : 0.25 }} className="ml-2">
                    <CratingRequiredIcon width={20} onClick={() => toggleItemProperty(index, "crating_required")} />
                  </span>
                </OverlayTrigger>
              )}
              {packingListConfig.temperature_controlled_enabled && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="item-tooltip">
                      <span className="font-weight-normal">
                        Temperature controlled{" "}
                        {selectedTempControlLabel && <div className="font-weight-bold">{selectedTempControlLabel}</div>}
                      </span>
                    </Tooltip>
                  }
                >
                  <span style={{ opacity: temperatureControlRequired ? 1 : 0.25 }} className="ml-1">
                    <TemperatureControlIcon
                      width={16}
                      onClick={() => toggleItemProperty(index, "temperature_control_required")}
                    />
                  </span>
                </OverlayTrigger>
              )}
              {packingListConfig.dangerous_goods_enabled && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="item-tooltip">
                      <span className="font-weight-normal">Dangerous Goods</span>
                      {dangerousGoodValue && (
                        <div className="font-weight-bold">
                          {dangerousGoodsOptions(intl).find((o) => o.value === dangerousGoodValue)?.label}
                        </div>
                      )}
                      {unNumber && (
                        <div className="font-weight-bold">
                          <FormattedMessage id="new-request.packing-list.unNumber" defaultMessage="UN Number" />:{" "}
                          {unNumber}
                        </div>
                      )}
                    </Tooltip>
                  }
                >
                  <span style={{ opacity: dangerous ? 1 : 0.25 }} className="ml-1">
                    <DangerousIcon width={17} onClick={() => toggleItemProperty(index, "is_dangerous_good")} />
                  </span>
                </OverlayTrigger>
              )}
              {handlingRequiredSetting && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="item-tooltip">
                      <span className="font-weight-normal">Handling required</span>
                    </Tooltip>
                  }
                >
                  <span style={{ opacity: handling_required ? 1 : 0.25 }} className="ml-2">
                    <HandlingRequiredIcon width={16} onClick={() => toggleItemProperty(index, "handling_required")} />
                  </span>
                </OverlayTrigger>
              )}
            </>
          );
        },
      },
      {
        Header: "",
        accessor: "index",
        actions: true,
        Cell: ({ value: itemReactHookFormIndex }: any) => (
          <>
            <span className="cursor-pointer">
              <CopyToClipboardIcon
                className="ml-2"
                onClick={() => {
                  clearSelectedItemAndCollapseRows(); // Prevents any unexpected side-effects when copying the item
                  append(getValues("packing_list")[itemReactHookFormIndex]);
                }}
              />
            </span>
            <span className="ml-2 cursor-pointer">
              <DeleteIcon
                onClick={() => {
                  clearSelectedItemAndCollapseRows(); // Prevents any unexpected side-effects when deleting the item
                  remove(itemReactHookFormIndex);
                }}
              />
            </span>
          </>
        ),
      },
    ];

    // Get the handling unit settings
    const handlingUnitObject = handlingUnits?.find((obj: any) =>
      Object.prototype.hasOwnProperty.call(obj, items[0].handling_unit),
    );

    // If we have the handling unit settings, add the columns required for that handling unit
    if (handlingUnitObject) {
      const firstPropertyName = Object.keys(handlingUnitObject)[0];
      const handlingUnitSettings = handlingUnitObject[firstPropertyName];

      // Add customs value and description columns
      if (handlingUnitSettings?.customs_value) {
        baseColumns.unshift(
          // @ts-ignore
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.customsValue",
              defaultMessage: "Customs value",
            }),
            accessor: "good_customs_information",
            Cell: ({ value }: any) => {
              if (!value || value?.length === 0) return <div />;
              // Reduce all the customs values into a single value
              let customsValue = 0;
              customsValue = value.reduce((acc: any, curr: any) => acc + parseFloat(curr.value), 0);
              if (isNaN(customsValue)) return <div />;
              return (
                <div>
                  {customsValue} {value[0].currency_code}
                </div>
              );
            },
          },
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.customsDescription",
              defaultMessage: "Customs description",
            }),
            accessor: (row: any) => [row.good_customs_information],
            Cell: ({ value }: any) => {
              // If there is no customs information, return an empty div
              if (!value || value?.length === 0 || !value[0]) return <div />;

              // If the user has uploaded a file for the customs description, return an empty div
              if (value[0][0]?.attachment?.length > 0) return <div />;

              // Get all the customs descriptions
              const customsDescriptions = value[0]
                .map((item: any) => item.description)
                .filter((desc: string) => desc !== null);

              return (
                <div>
                  {customsDescriptions[0]}{" "}
                  {customsDescriptions.length > 1 && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="item-tooltip">
                          {customsDescriptions.slice(1).map((desc: string) => (
                            <div key={desc}>{desc}</div>
                          ))}
                        </Tooltip>
                      }
                    >
                      <span className="text-secondary ml-1">+{customsDescriptions.length - 1}</span>
                    </OverlayTrigger>
                  )}
                </div>
              );
            },
          },
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.hsCode",
              defaultMessage: "HS Code",
            }),
            accessor: (row: any) => [row.good_customs_information],
            Cell: ({ value }: any) => {
              // If there is no customs information, return an empty div
              if (!value || value?.length === 0 || !value[0]) return <div />;

              // If the user has uploaded a file for the customs description, return an empty div
              if (value[0][0]?.attachment?.length > 0) return <div />;

              // Get all the customs descriptions
              const hsCodes = value[0].map((item: any) => item.hs_code).filter((desc: string) => desc !== null);

              return (
                <div>
                  {hsCodes[0]}{" "}
                  {hsCodes.length > 1 && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="item-tooltip">
                          {hsCodes.slice(1).map((desc: string) => (
                            <div key={desc}>{desc}</div>
                          ))}
                        </Tooltip>
                      }
                    >
                      <span className="text-secondary ml-1">+{hsCodes.length - 1}</span>
                    </OverlayTrigger>
                  )}
                </div>
              );
            },
          },
        );
      }

      // Add Pickup and Delivery columns for multi-stop
      if (hasMultiStopStops) {
        const getStopLabel = (stopKey: string, stopType: number) => {
          if (!stopKey) return "";
          const stop = stops.find((s: any) => s.stop_key === stopKey && s.stop_type === stopType);
          if (!stop)
            return (
              <div className="missing-pickup-delivery-text">
                {intl.formatMessage({ id: "general.missing", defaultMessage: "Missing" })}
              </div>
            );
          return stop?.address_name || stop?.street_address || null;
        };

        baseColumns.unshift(
          {
            Header: intl.formatMessage({
              id: "general.pickup",
              defaultMessage: "Pickup",
            }),
            accessor: "pickup_stop_key",
            // @ts-ignore
            sortable: false,
            Cell: ({ value }: any) => {
              const label = getStopLabel(value, StopType.PICKUP);
              return (
                <div className="text-truncate" style={{ maxWidth: 118 }}>
                  {label}
                </div>
              );
            },
          },
          {
            Header: intl.formatMessage({
              id: "general.delivery",
              defaultMessage: "Delivery",
            }),
            accessor: "delivery_stop_key",
            // @ts-ignore
            sortable: false,
            Cell: ({ value }: any) => {
              const label = getStopLabel(value, StopType.DROPOFF);
              return (
                <div className="text-truncate" style={{ maxWidth: 118 }}>
                  {label}
                </div>
              );
            },
          },
        );
      }

      // Get the NMFC class status for the selected handling unit
      const getNMFCSetting = () => {
        if (handlingUnitSettings?.nmfc === "Location settings") {
          return isNMFCEnabled;
        }
        return handlingUnitSettings?.nmfc;
      };

      // Add NMFC column
      if (getNMFCSetting()?.toLowerCase?.() !== "off") {
        baseColumns.unshift({
          Header: intl.formatMessage({
            id: "quote.nmfc",
            defaultMessage: "NMFC",
          }),
          accessor: "nmfc_class",
          // @ts-ignore
          sortable: true,
        });
      }

      // Add weight column
      if (handlingUnitSettings.weight !== "off") {
        baseColumns.unshift({
          Header: intl.formatMessage({
            id: "quotation-form.viewCommodities.weight",
            defaultMessage: "Weight",
          }),
          // @ts-ignore
          accessor: (row: any) => [row.weight_piece, row.w_unit],
          // @ts-ignore
          sortable: true,
          Cell: ({ value }: any) => {
            const [weightPiece, weightUnit] = value ?? [];
            const effectiveUnit =
              weightUnit ?? getValues("packing.global_configuration.w_unit") ?? "";
            const isTotalWeight = effectiveUnit === "total_lbs" || effectiveUnit === "total_kg";
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [weightPrefix, weightSuffix] = effectiveUnit.split("_");
            return (
              <div>
                {weightPiece}
                {isTotalWeight ? `${weightSuffix} (total)` : `${effectiveUnit} (piece)`}
              </div>
            );
          },
        });
      }

      // Add units column
      if (handlingUnitSettings?.quantity !== "off") {
        baseColumns.unshift({
          Header: intl.formatMessage({
            id: "new-request.packing-list.numberOfUnits",
            defaultMessage: "Quantity",
          }),
          accessor: "units",
          // @ts-ignore
          sortable: true,
        });
      }

      // Add LxWxH columns
      if (handlingUnitSettings.label !== "container" && handlingUnitSettings.l_w_h !== "off") {
        baseColumns.unshift(
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.length",
              defaultMessage: "Length",
            }),
            accessor: (row: any) => [row.length, row.m_unit],
            // @ts-ignore
            sortable: true,
          },
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.width",
              defaultMessage: "Width",
            }),
            accessor: (row: any) => [row.width, row.m_unit],
            sortable: true,
          },
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.height",
              defaultMessage: "Height",
            }),
            accessor: (row: any) => [row.height, row.m_unit],
            sortable: true,
          },
        );
      }

      if (handlingUnitSettings?.additional_field !== "off") {
        baseColumns.unshift({
          Header:
            handlingUnitSettings?.additional_field_label ??
            intl.formatMessage({
              id: "new-request.packing-list.additionalField",
              defaultMessage: "Additional Field",
            }),
          accessor: "additional_field",
          // @ts-ignore
          sortable: true,
        });
      }

      // Add CBM columns
      if (handlingUnitSettings?.l_w_h_cbm) {
        baseColumns.unshift(
          {
            Header: intl.formatMessage({
              id: "general.cbm",
              defaultMessage: "CBM",
            }),
            // @ts-ignore
            accessor: (row: any) => [row.l_w_h_cbm_piece],
            // @ts-ignore
            sortable: true,
            Cell: ({ value }: any) => {
              const cbmPiece = value[0];
              return <div>{cbmPiece}</div>;
            },
          },
          {
            Header: intl.formatMessage({
              id: "new-request.packing-list.cubicScope",
              defaultMessage: "CBM Scope",
            }),
            accessor: "cbm_scope",
            // @ts-ignore
            sortable: true,
          },
        );
      }

      // Add container type column
      if (handlingUnitSettings?.label === "container") {
        baseColumns.unshift({
          Header: intl.formatMessage({
            id: "general.type",
            defaultMessage: "Type",
          }),
          accessor: "container_type",
          // @ts-ignore
          sortable: true,
          Cell: ({ value }: any) => {
            const containerType = handlingUnitSettings?.handling_unit_options.find((c: any) => c.key === value)?.value;
            return <div>{containerType}</div>;
          },
        });
      }

      if (errors?.packing_list || hasWarnings) {
        baseColumns.unshift({
          id: "warnings",
          accessor: (row: any) => [row.index],
          Cell: ({ value }: any) => {
            const [index] = value;
            if (packingListStopWarnings?.[index]) {
              return (
                <div className="missing-pickup-delivery-table-warning-icon" style={{ marginRight: "-8px" }}>
                  <AlertTriangle height={14} width={14} />
                </div>
              );
            }

            if (errors?.packing_list?.[index])
              return (
                <div className="valid-status error" style={{ marginRight: "-8px" }}>
                  <ExclamationIcon height={14} width={14} />
                </div>
              );
            return <div />;
          },
          // @ts-ignore
          sortable: false,
        });
      }
    }
    return baseColumns;
  }, [errors?.packing_list, stops, hasWarnings, packingListStopWarnings]);

  // This function is called when the user clicks on a row
  const onRowClick = (row: Row<any>, e: any) => {
    if (["svg", "path", "rect"].includes(e.target.nodeName.toLowerCase())) return; // ignore clicks on action icons

    // Close the current expanded row if it is open
    if (expandedRow && expandedRow !== row && expandedRow.isExpanded) expandedRow.toggleRowExpanded();

    // Set selected item index
    setSelectedItemIndex(row.original.index);
    e.stopPropagation();

    // Save the row as the last expanded row
    setExpandedRow(row);

    // Clear the selected item index if the row is open and the user clicks on it
    if (row.isExpanded) setSelectedItemIndex(null);

    row.toggleRowExpanded();
  };

  const instance = useTable(
    {
      columns,
      data: items,
    },
    useSortBy,
    useExpanded,
  );

  useEffect(() => {
    if (hasOneHsCode) {
      instance.setHiddenColumns([]);
    } else {
      instance.setHiddenColumns(["HS Code"]);
    }
  }, [hasOneHsCode]);

  return (
    <div className="d-flex packing-list-table-container mb-4">
      <p className="font-b4 w-75-px" style={{ marginTop: 17 }}>
        {capitalizeFirstLetter(handlingUnit)}
      </p>
      <DataTable
        tableInstance={instance}
        totalData={items.length}
        draggable={false}
        RowSubComponent={({ row }: any) => (
          <PackingListAddItem
            handlingUnits={handlingUnits}
            append={append}
            isNMFCEnabled={isNMFCEnabled}
            NMFCValues={NMFCValues}
            additionalInfoPlaceholder={packingListConfig.additional_info_description}
            selectedItemIndex={row.original.index}
            setSelectedItemIndex={setSelectedItemIndex}
            expandedRow={expandedRow}
            setExpandedRow={setExpandedRow}
            getRequestFormValues={getValues}
            setRequestFormValue={setValue}
            watchRequestFormValues={watch}
            currencyOptions={currencyOptions}
            state={state}
            dispatch={dispatch}
            isQuoteRequest={isQuoteRequest}
          />
        )}
        onRowClick={onRowClick}
      />
    </div>
  );
}

export default PackingListTable;
